import React from 'react';
import PropTypes from 'prop-types';

import { appConfig } from 'brand-config';

import branchLogo from './branch-logo.svg';
import emberLogo from './ember-logo.svg';
import useBranchSpinnerStyles from './branch-spinner.styles';
import useEmberSpinnerStyles from './ember-expressive-dot-spinner.styles';

// Color stops for the Ember expressive dot spinner — taken from design's
// dev-handoff.html, ordered around the orbit via the `--mid-angle` CSS var.
const EMBER_DOTS = [
  { midAngle: -108, background: '#e1e43e' },
  { midAngle: -96, background: '#c8cf52' },
  { midAngle: -82, background: '#e1e43e' },
  { midAngle: -64, background: '#96b74f' },
  { midAngle: -42, background: '#6d9152' },
  { midAngle: -18, background: '#4a819b' },
  { midAngle: 0, background: '#4a819b' },
  { midAngle: 22, background: '#376d8a' },
  { midAngle: 46, background: '#275a79' },
  { midAngle: 70, background: '#1b4765' },
  { midAngle: 90, background: '#0f3855' },
  { midAngle: 108, background: '#072c49' }
];

// Logo-less ring spinner in Branch colors. Used when a brand has no spinner
// configured — see issue #30793 design gif.
function GenericSpinner() {
  const classes = useBranchSpinnerStyles();
  return (
    <div className={classes.wrap} aria-label="Loading">
      <svg className={classes.svg} viewBox="0 0 112 112" aria-hidden="true">
        <circle className={classes.ringBase} cx="56" cy="56" r="50" />
        <circle className={classes.ringArc} cx="56" cy="56" r="50" />
      </svg>
    </div>
  );
}

function BranchSpinner() {
  const classes = useBranchSpinnerStyles();
  return (
    <div className={classes.wrap} aria-label="Loading">
      <svg className={classes.svg} viewBox="0 0 112 112" aria-hidden="true">
        <circle className={classes.ringBase} cx="56" cy="56" r="50" />
        <circle className={classes.ringArc} cx="56" cy="56" r="50" />
      </svg>
      <img className={classes.center} src={branchLogo} alt="" aria-hidden="true" />
    </div>
  );
}

function EmberSpinner() {
  const classes = useEmberSpinnerStyles();
  return (
    <div className={classes.wrap} aria-label="Loading">
      <div className={classes.orbit} aria-hidden="true">
        <div className={classes.dotTrack}>
          {EMBER_DOTS.map(({ midAngle, background }) => (
            <span key={midAngle} className={classes.dot} style={{ '--mid-angle': midAngle, background }} />
          ))}
        </div>
      </div>
      <img className={classes.center} src={emberLogo} alt="" aria-hidden="true" />
    </div>
  );
}

function pickVariant() {
  switch (appConfig?.spinner) {
    case 'branch':
      return BranchSpinner;
    case 'ember':
      return EmberSpinner;
    default:
      return GenericSpinner;
  }
}

function Spinner({ size, className }) {
  const style = size ? { '--spinner-size': `${size}px` } : undefined;
  const Variant = pickVariant();
  return (
    <div className={className} style={style}>
      <Variant />
    </div>
  );
}

Spinner.propTypes = {
  size: PropTypes.number,
  className: PropTypes.string
};

Spinner.defaultProps = {
  size: undefined,
  className: ''
};

export default Spinner;
